import {useEffect, useState} from "react";
import CustomLoader from "../../CustomComponents/CustomLoader";

import {useLocation} from "react-router-dom";
import {GetCommentHistoryService, GetReplies} from "../../services/contractorServices";

import Modal from "@mui/material/Modal";
import Accordion from "@mui/material/Accordion";
import Typography from "@mui/material/Typography";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";

import Rating from "@mui/material/Rating";
import CustomUserName from "../../CustomComponents/CustomUserName";
import {fDate} from "../../utils/format-time";
import {truncateWords} from "../../utils/format-text";
import {Flex} from "antd";
import CustomUserProfile from "../../CustomComponents/CustomUserProfile";
import Divider from "@mui/material/Divider";

const CommentHistory = () => {
	const location = useLocation();

	const [loading, setLoading] = useState(true);

	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(10);
	const [list, setList] = useState([]);
	const [addressList, setAddressList] = useState([]);

	const [filterStatus, setFilterStatus] = useState(location.state?.filter || "all");

	// Track which items have comments expanded
	const [expandedComments, setExpandedComments] = useState(new Set());

	// Modal states
	const [likesModalOpen, setLikesModalOpen] = useState(false);
	const [commentsModalOpen, setCommentsModalOpen] = useState(false);
	const [upliftsModalOpen, setUpliftsModalOpen] = useState(false);
	const [selectedLikes, setSelectedLikes] = useState([]);
	const [selectedComments, setSelectedComments] = useState([]);
	const [selectedUplifts, setSelectedUplifts] = useState([]);
	const [modalTitle, setModalTitle] = useState("");
	const [selectedActivity, setSelectedActivity] = useState(null);

	const getMockRatings = async () => {
		try {
			setLoading(true);
			const response = await GetReplies({});

			if (response?.status) {
				setList(response?.data?.list);
			}
		} catch (error) {
			console.error("Error fetching activity list:", error);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		getMockRatings();
	}, [page, pageSize]);

	// Calculate total activities count
	const getTotalActivities = () => {
		return list.reduce((total, item) => total + (item?.list?.length || 0), 0);
	};

	// Calculate average rating for an address
	const getAverageRating = (addressList) => {
		const ratings = addressList?.filter((item) => item?.rating_id && item?.stars);
		if (ratings.length === 0) return 0;
		const sum = ratings.reduce((acc, item) => acc + item.stars, 0);
		return sum / ratings.length;
	};

	// Format date
	const formatDate = (dateString) => {
		return new Date(dateString).toLocaleDateString("en-US", {
			year: "numeric",
			month: "short",
			day: "numeric",
		});
	};

	// Format date and time
	const formatDateTime = (dateString) => {
		return new Date(dateString).toLocaleString("en-US", {
			year: "numeric",
			month: "short",
			day: "numeric",
			hour: "2-digit",
			minute: "2-digit",
			hour12: true,
		});
	};

	// Format job issues
	const formatJobIssue = (issue) => {
		return issue.replace(/-/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
	};

	// Filter list based on selected address
	const filteredList = filterStatus === "all" ? list : list?.filter((item) => item);

	// Handle likes modal
	const handleShowLikes = (likes, title, activity) => {
		setSelectedLikes(likes || []);
		setModalTitle(`${title} - Likes`);
		setSelectedActivity(activity);
		setLikesModalOpen(true);
	};

	// Handle comments toggle for inline display
	const handleToggleComments = (index) => {
		setExpandedComments((prev) => {
			const newSet = new Set(prev);
			if (newSet.has(index)) {
				newSet.delete(index);
			} else {
				newSet.add(index);
			}
			return newSet;
		});
	};

	// Handle comments modal
	const handleShowComments = (comments, title, activity) => {
		setSelectedComments(comments || []);
		setModalTitle(`${title} - Comments`);
		setSelectedActivity(activity);
		setCommentsModalOpen(true);
	};

	// Handle uplifts modal
	const handleShowUplifts = (uplifts, title, activity) => {
		setSelectedUplifts(uplifts || []);
		setModalTitle(`${title} - Uplifts`);
		setSelectedActivity(activity);
		setUpliftsModalOpen(true);
	};

	// Get user initials for avatar
	const getUserInitials = (firstName, lastName) => {
		return `${firstName?.charAt(0) || ""}${lastName?.charAt(0) || ""}`.toUpperCase();
	};

	// Modal styles
	const modalStyle = {
		position: "absolute",
		top: "50%",
		left: "50%",
		transform: "translate(-50%, -50%)",
		width: "90%",
		bgcolor: "background.paper",
		// border: "2px solid #000",
		boxShadow: 24,
		p: 4,
		borderRadius: 2,
		maxHeight: "80vh",
		overflow: "auto",
	};

	return (
		<div className="dashboard-page">
			<div className="page-header">
				{/* <h2>Comment History</h2> */}
				<h2>Replies</h2>
			</div>

			{loading ? (
				<div className="ratings-container">
					<CustomLoader />
				</div>
			) : (
				<div className="ratings-controls">
					{list && list.length > 0 ? (
						list.map((item, index) => {
							return (
								<div key={index} className="my-1 rating-card">
									<div className={`border-success border-2 border-0  bg-light `}>
										<div className="p-3">
											<div className="row align-items-center">
												<div className="col-md-9">
													<div className="mb-2">
														<strong>{item?.title}</strong>
													</div>
													<div className="mb-2">
														<strong> {item?.addresses}</strong>
													</div>
													<div className="text-muted">{item?.description || "No description provided"}</div>
												</div>
												<div className="col-md-3">
													<div className="text-end">
														<div className="text-muted small mb-1">Job Date: {fDate(item?.comment_date)}</div>
														<div className="d-flex justify-content-end ">
															<CustomUserName user={item?.user} sx={{fontSize: 14, fontWeight: 600}} />
														</div>
														<div className="mt-1">
															<Chip
																label={`${item?.street_comments?.length || 0} Comment${(item?.street_comments?.length || 0) !== 1 ? "s" : ""}`}
																size="small"
																color={expandedComments.has(index) ? "primary" : "info"}
																// onClick={() => handleToggleComments(index)}
																clickable
																sx={{mx: 1}}
															/>
														</div>
														{/* <Chip label={`${item?.likes?.length || 0} Likes`} size="small" color="primary" onClick={() => handleShowLikes(item?.likes, `${item?.client_name} Comment`, item)} clickable /> */}
													</div>
												</div>
											</div>
										</div>

										{item?.street_comments && item?.street_comments.length > 0 ? (
											<div>
												<Divider />
												{item.street_comments.map((comment, commentIndex) => (
													<div key={commentIndex} className="d-flex align-items-start py-2 px-2 mb-2 bg-light rounded">
														<div className="me-2">
															<CustomUserProfile user={comment?.user} width={32} sx={{fontSize: 13}} />
														</div>
														<div className="flex-grow-1">
															<div className="fw-semibold small mb-1">
																<CustomUserName user={comment?.user} sx={{fontWeight: 600}} />
															</div>
															<div className="text-muted small mb-2" style={{fontSize: "0.65rem"}}>
																{formatDateTime(comment?.createdAt)}
															</div>
															<div className="text-dark">{comment?.description}</div>
														</div>
													</div>
												))}
											</div>
										) : expandedComments.has(index) && (!item?.street_comments || item?.street_comments.length === 0) ? (
											<div>
												<Divider />
												<div className="text-center py-4">
													<div className="text-muted">No comments found</div>
												</div>
											</div>
										) : null}
									</div>
								</div>
							);
						})
					) : (
						<div className="text-center py-5">
							<div className="text-muted">No activities found</div>
						</div>
					)}
				</div>
			)}

			{/* Likes Modal */}
			<Modal open={likesModalOpen} onClose={() => setLikesModalOpen(false)}>
				<Box sx={modalStyle}>
					<Typography variant="h6" component="h2" className="mb-3">
						{modalTitle}
					</Typography>
					{selectedLikes && selectedLikes.length > 0 ? (
						<div>
							{selectedLikes.map((like, index) => (
								<div key={index} className="d-flex align-items-center py-2 px-2 mb-2 bg-light rounded">
									<div className="me-2">
										<Avatar variant="rounded" sx={{bgcolor: "#22c55e1a", color: "#28a745"}}>
											<div className="bi-hand-thumbs-up" style={{fontSize: 20}} />
										</Avatar>
									</div>
									<div className="flex-grow-1">
										<div className="fw-semibold small">
											<CustomUserName user={like?.user} sx={{fontWeight: 600}} />
										</div>
										<div className="text-muted" style={{fontSize: "0.65rem"}}>
											{formatDateTime(like?.createdAt)}
										</div>
									</div>
								</div>
							))}
						</div>
					) : (
						<div className="text-center py-4">
							<div className="text-muted">No likes found</div>
						</div>
					)}
				</Box>
			</Modal>

			{/* Comments Modal */}
			<Modal open={commentsModalOpen} onClose={() => setCommentsModalOpen(false)}>
				<Box sx={modalStyle}>
					<Typography variant="h6" component="h2" className="mb-3">
						{modalTitle}
					</Typography>
					{selectedComments && selectedComments.length > 0 ? (
						<div>
							{selectedComments.map((comment, index) => (
								<div key={index} className="d-flex align-items-start py-2 px-2 mb-2 bg-light rounded">
									<div className="me-2">
										{/* <Avatar variant="rounded" sx={{bgcolor: "#0dcaf01a", color: "#0dcaf0"}}>
											<svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
												<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
											</svg>
										</Avatar> */}
										<CustomUserProfile user={comment?.user} width={32} sx={{fontSize: 13}} />
									</div>
									<div className="flex-grow-1">
										<div className="fw-semibold small mb-1">
											<CustomUserName user={comment?.user} sx={{fontWeight: 600}} />
										</div>
										<div className="text-muted small mb-2" style={{fontSize: "0.65rem"}}>
											{formatDateTime(comment?.createdAt)}
										</div>
										<div className="text-dark">{comment?.description}</div>
									</div>
								</div>
							))}
						</div>
					) : (
						<div className="text-center py-4">
							<div className="text-muted">No comments found</div>
						</div>
					)}
				</Box>
			</Modal>
		</div>
	);
};

export default CommentHistory;
